/**
 * Device speed benchmark ("stress test") — estimates how many mockups this machine
 * can render per minute by TIMING THE REAL ENGINE (genz-engine) render path.
 *
 * The current production render is the LAYERED MOCKUP COMPOSITE (spec 019):
 * `engine.composeMockup(...)` blends a fill stack over a baked shell/ground/print
 * area — this dominates real render cost. `runLayeredSpeedBenchmark` synthesizes
 * representative inputs once, then drives `composeMockup` as many times as fit in a
 * time budget and extrapolates to mockups/minute. It ALSO times the edit-time "bake"
 * ops once (NOT in the loop): the magenta-marker CV detect (always) and the BiRefNet
 * AI matte (desktop-native only; reported N/A on web).
 *
 * It is a COMPUTE score (no network, no provider call) — comparable across machines
 * and good for "is this box fast enough for bulk batches?". The render engine is
 * CPU-bound (Rust/WASM, no GPU), so the reported compute MODE is CPU; we also detect
 * the machine's core count and GPU for context.
 *
 * The legacy `runSpeedBenchmark` (a pure-JS warp+blend microbenchmark that never
 * touched the engine) is kept below for back-compat but is no longer used by the UI.
 */
import type { ComposeMockupArgs, EngineBinding, Polygon } from "@genz/engine-bind";
export interface SpeedOptions {
  /** Resolution per side, px (the mockup is side×side). Default 1024. */
  side?: number;
  /** Design images composited per mockup (2–10). Default 2. */
  imagesPerMockup?: number;
  /** Time budget for the timed loop, ms. Default 1500. */
  budgetMs?: number;
}

/** Compute environment the test ran in. */
export interface ComputeInfo {
  /** Active mode for the render path: always "CPU" (the engine is CPU-bound). */
  mode: "CPU" | "GPU" | "CPU+GPU";
  /** Logical CPU cores (navigator.hardwareConcurrency), if known. */
  cores: number | null;
  /** Detected GPU renderer string (WebGL), or null — informational (not used to render). */
  gpu: string | null;
}

/** Timings for the edit-time "bake" ops, measured ONCE (outside the compose loop). */
export interface BakeTimings {
  /** `detectMagentaMarker` — the pure-CV print-area marker keyer (always measured). */
  markerDetect: number;
  /** `removeBackgroundAI` — the BiRefNet AI matte. DESKTOP-NATIVE ONLY; undefined on
   *  web (the WASM binding omits it) or when the op throws / is unavailable. */
  removeBackground?: number;
}

export interface SpeedResult {
  /** Representative render passes completed during the run (= composeMockup calls). */
  passes: number;
  /** Wall-clock spent in the timed compose loop, ms. */
  elapsedMs: number;
  /** Milliseconds for one mockup (one `composeMockup` call). */
  msPerMockup: number;
  /** Extrapolated throughput. */
  mockupsPerMin: number;
  /** Megapixels/second processed. */
  mpixPerSec: number;
  /** 0–100 score (re-baselined against composeMockup throughput; see formula below). */
  score: number;
  /** Echo of the parameters used. */
  side: number;
  imagesPerMockup: number;
  /** Compute environment. */
  compute: ComputeInfo;
  /** One-shot bake-phase timings (marker detect + optional AI matte). */
  bakeMs: BakeTimings;
  /** Which engine impl ran the benchmark ("native" desktop / "wasm" web), or null. */
  engineImpl: EngineBinding["impl"] | null;
}

export const MIN_IMAGES = 2;
export const MAX_IMAGES = 10;
/** Selectable resolutions (per side, px). */
export const RESOLUTIONS = [512, 1024, 2048, 4096] as const;

/** Best-effort GPU renderer string via WebGL (informational only). */
function detectGpu(): string | null {
  if (typeof document === "undefined") return null;
  try {
    const canvas = document.createElement("canvas");
    const gl = (canvas.getContext("webgl") ?? canvas.getContext("experimental-webgl")) as WebGLRenderingContext | null;
    if (!gl) return null;
    const dbg = gl.getExtension("WEBGL_debug_renderer_info");
    if (dbg) {
      const r = gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL);
      if (typeof r === "string" && r) return r;
    }
    const v = gl.getParameter(gl.RENDERER);
    return typeof v === "string" ? v : null;
  } catch {
    return null;
  }
}

function detectCompute(): ComputeInfo {
  const cores = (typeof navigator !== "undefined" && typeof navigator.hardwareConcurrency === "number")
    ? navigator.hardwareConcurrency : null;
  // The render path is CPU-bound (genz-engine); GPU is detected for context only.
  return { mode: "CPU", cores, gpu: detectGpu() };
}

/** One representative mockup pass: warp-sample + alpha composite, repeated for each
 *  design image layered onto the mockup. */
function renderPass(dst: Uint8ClampedArray, design: Uint8ClampedArray, side: number, layers: number): void {
  const w = side;
  const mask = side * side * 4 - 4;
  for (let layer = 0; layer < layers; layer++) {
    for (let y = 0; y < side; y++) {
      for (let x = 0; x < w; x++) {
        const o = (y * w + x) * 4;
        const denom = 1 + (x + y) * 0.0001;
        const sx = ((x * 0.98) / denom) | 0;
        const sy = ((y * 0.98) / denom) | 0;
        const s = ((sy * w + sx) * 4) & mask;
        const a = design[s + 3] / 255;
        dst[o] = (design[s] * a + dst[o] * (1 - a)) | 0;
        dst[o + 1] = (design[s + 1] * a + dst[o + 1] * (1 - a)) | 0;
        dst[o + 2] = (design[s + 2] * a + dst[o + 2] * (1 - a)) | 0;
      }
    }
  }
}

/** Run the benchmark and return a speed score for the chosen parameters. */
export function runSpeedBenchmark(opts: SpeedOptions | number = {}): SpeedResult {
  // Back-compat: a bare number used to mean budgetMs.
  const o: SpeedOptions = typeof opts === "number" ? { budgetMs: opts } : opts;
  const side = o.side ?? 1024;
  const imagesPerMockup = Math.max(MIN_IMAGES, Math.min(MAX_IMAGES, o.imagesPerMockup ?? MIN_IMAGES));
  const budgetMs = o.budgetMs ?? 1500;
  const pixels = side * side;

  const dst = new Uint8ClampedArray(pixels * 4);
  const design = new Uint8ClampedArray(pixels * 4);
  for (let i = 0; i < pixels * 4; i++) { dst[i] = (i * 7) & 255; design[i] = (i * 13) & 255; }

  // Warm-up (let the JIT compile the hot loop before timing).
  renderPass(dst, design, side, imagesPerMockup);

  const start = (typeof performance !== "undefined" ? performance.now() : Date.now());
  let passes = 0;
  let now = start;
  do {
    renderPass(dst, design, side, imagesPerMockup);
    passes++;
    now = (typeof performance !== "undefined" ? performance.now() : Date.now());
  } while (now - start < budgetMs);

  const elapsedMs = now - start;
  const msPerMockup = elapsedMs / passes;
  const mockupsPerMin = (60_000 / msPerMockup);
  // mpix counts every composited layer at the chosen resolution.
  const mpixPerSec = (passes * pixels * imagesPerMockup) / (elapsedMs / 1000) / 1_000_000;
  // Normalise the score against the 1024²×2 baseline so it stays comparable.
  const work = (pixels * imagesPerMockup) / (1024 * 1024 * 2);
  const baselineRate = mockupsPerMin * work; // equivalent baseline mockups/min
  const score = Math.max(0, Math.min(100, Math.round((baselineRate / 300) * 100)));
  return {
    passes, elapsedMs, msPerMockup, mockupsPerMin, mpixPerSec, score,
    side, imagesPerMockup, compute: detectCompute(),
    // Legacy JS microbenchmark never touched the engine — no bake phase, no impl.
    bakeMs: { markerDetect: 0 }, engineImpl: null,
  };
}

// ---------------------------------------------------------------------------
// Layered-compositing benchmark — TIMES THE REAL ENGINE (current render flow).
// ---------------------------------------------------------------------------

/** True when an offscreen 2D canvas with toDataURL is available (guards jsdom/no-DOM). */
function canvasAvailable(): boolean {
  if (typeof document === "undefined") return false;
  try {
    const c = document.createElement("canvas");
    const ctx = c.getContext?.("2d");
    return !!ctx && typeof c.toDataURL === "function" && typeof ctx.putImageData === "function";
  } catch {
    return false;
  }
}

/** Decode a `data:…;base64,…` URL to raw bytes. */
function dataUrlToBytes(dataUrl: string): Uint8Array {
  const b64 = dataUrl.slice(dataUrl.indexOf(",") + 1);
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/** Encode an RGBA buffer to PNG bytes via an offscreen canvas (caller guards canvas). */
function rgbaToPng(rgba: Uint8ClampedArray, side: number): Uint8Array {
  const c = document.createElement("canvas");
  c.width = side;
  c.height = side;
  const ctx = c.getContext("2d")!;
  const img = ctx.createImageData(side, side);
  img.data.set(rgba);
  ctx.putImageData(img, 0, 0);
  return dataUrlToBytes(c.toDataURL("image/png"));
}

/** Synthesize a side×side RGBA buffer with a deterministic-but-varied pattern and a
 *  centered opaque rectangle (so masks/quads have something real to clip). */
function synthRgba(side: number, seed: number): Uint8ClampedArray {
  const rgba = new Uint8ClampedArray(side * side * 4);
  const lo = (side / 4) | 0;
  const hi = side - lo;
  for (let y = 0; y < side; y++) {
    for (let x = 0; x < side; x++) {
      const o = (y * side + x) * 4;
      rgba[o] = (x * 3 + seed) & 255;
      rgba[o + 1] = (y * 5 + seed * 7) & 255;
      rgba[o + 2] = ((x ^ y) + seed * 11) & 255;
      rgba[o + 3] = 255;
    }
  }
  // Punch a translucent center band so alpha-compositing has work to do.
  for (let y = lo; y < hi; y++) {
    for (let x = lo; x < hi; x++) {
      rgba[(y * side + x) * 4 + 3] = 200;
    }
  }
  return rgba;
}

/** Raw `side*side` coverage buffer: 255 inside a centered rectangle, 0 outside. */
function synthMask(side: number): Uint8Array {
  const m = new Uint8Array(side * side);
  const lo = (side / 4) | 0;
  const hi = side - lo;
  for (let y = lo; y < hi; y++) {
    for (let x = lo; x < hi; x++) m[y * side + x] = 255;
  }
  return m;
}

/** Normalized print quad over the centered rect (TL, TR, BR, BL). */
function centeredQuad(): Polygon {
  return [
    { x: 0.25, y: 0.25 },
    { x: 0.75, y: 0.25 },
    { x: 0.75, y: 0.75 },
    { x: 0.25, y: 0.75 },
  ];
}

const now = () => (typeof performance !== "undefined" ? performance.now() : Date.now());

/** A zeroed sentinel result for environments without a usable canvas (jsdom/tests). */
function emptyResult(side: number, imagesPerMockup: number, impl: EngineBinding["impl"] | null): SpeedResult {
  return {
    passes: 0, elapsedMs: 0, msPerMockup: 0, mockupsPerMin: 0, mpixPerSec: 0, score: 0,
    side, imagesPerMockup, compute: detectCompute(),
    bakeMs: { markerDetect: 0 }, engineImpl: impl,
  };
}

/**
 * Benchmark the REAL current render flow by timing `engine.composeMockup`.
 *
 * Synthesizes representative inputs ONCE (generated, shell, productMask, printMask,
 * printQuad, and `imagesPerMockup` design fills), then calls `composeMockup` in a loop
 * until `budgetMs` elapses, counting passes. Also times the bake ops once: the magenta
 * marker CV detect (always) and the AI matte (`removeBackgroundAI`, desktop only).
 */
export async function runLayeredSpeedBenchmark(
  engine: EngineBinding,
  opts: SpeedOptions = {},
): Promise<SpeedResult> {
  const side = opts.side ?? 1024;
  const imagesPerMockup = Math.max(MIN_IMAGES, Math.min(MAX_IMAGES, opts.imagesPerMockup ?? MIN_IMAGES));
  const budgetMs = opts.budgetMs ?? 1500;
  const impl = engine?.impl ?? null;

  // Guard for jsdom / no-canvas (and a binding without composeMockup): return a
  // zeroed sentinel rather than crashing.
  if (!canvasAvailable() || typeof engine?.composeMockup !== "function") {
    return emptyResult(side, imagesPerMockup, impl);
  }

  // Synthesize the layered-compositor inputs ONCE.
  const generated = rgbaToPng(synthRgba(side, 1), side);
  const shell = rgbaToPng(synthRgba(side, 2), side);
  const productMask = synthMask(side);
  const printMask = synthMask(side);
  const printQuad = centeredQuad();
  const fills: Uint8Array[] = [];
  const fillOpacity: number[] = [];
  for (let i = 0; i < imagesPerMockup; i++) {
    fills.push(rgbaToPng(synthRgba(side, 100 + i), side));
    fillOpacity.push(1);
  }
  const composeArgs: ComposeMockupArgs = {
    generated, shell, productMask, printMask, printQuad,
    fills, fillOpacity, background: null, behind: null,
  };

  // --- Bake phase (timed ONCE, outside the compose loop) -------------------
  const bakeMs: BakeTimings = { markerDetect: 0 };
  if (typeof engine.detectMagentaMarker === "function") {
    try {
      const t0 = now();
      await engine.detectMagentaMarker(generated, 0);
      bakeMs.markerDetect = now() - t0;
    } catch {
      bakeMs.markerDetect = 0;
    }
  }
  // AI matte: desktop-native only — the WASM binding omits removeBackgroundAI. Wrap in
  // try/catch and leave `removeBackground` undefined (→ "desktop only / N/A") on web.
  if (typeof engine.removeBackgroundAI === "function") {
    try {
      const t0 = now();
      // The native segmenter uses the model cached via loadSegmentationModel. We don't load a
      // model here, so this times the matte only if the editor already loaded one this session;
      // otherwise it throws ("model not loaded") and we report N/A.
      await engine.removeBackgroundAI(generated);
      bakeMs.removeBackground = now() - t0;
    } catch {
      // Unavailable / failed — leave undefined so the UI shows N/A.
    }
  }

  // --- Compose loop (timed) ------------------------------------------------
  // Warm-up once (let the engine / JIT settle before timing).
  await engine.composeMockup(composeArgs);

  const start = now();
  let passes = 0;
  let elapsed = 0;
  do {
    await engine.composeMockup(composeArgs);
    passes++;
    elapsed = now() - start;
  } while (elapsed < budgetMs);

  const pixels = side * side;
  const elapsedMs = elapsed;
  const msPerMockup = elapsedMs / passes;
  const mockupsPerMin = 60_000 / msPerMockup;
  // mpix counts the product canvas once per compose (the compositor's dominant pass).
  const mpixPerSec = (passes * pixels) / (elapsedMs / 1000) / 1_000_000;
  // Score: re-baselined against composeMockup throughput. The engine compose is far
  // heavier than the old JS warp, so the reference is 60 mockups/min at 1024² (≈1s
  // each) → 100. Normalize by resolution work vs the 1024² baseline so the score stays
  // comparable across the resolution dropdown; linear, capped 0..100.
  const COMPOSE_BASELINE_MPM = 60;
  const work = pixels / (1024 * 1024);
  const baselineRate = mockupsPerMin * work; // equivalent 1024² mockups/min
  const score = Math.max(0, Math.min(100, Math.round((baselineRate / COMPOSE_BASELINE_MPM) * 100)));

  return {
    passes, elapsedMs, msPerMockup, mockupsPerMin, mpixPerSec, score,
    side, imagesPerMockup, compute: detectCompute(),
    bakeMs, engineImpl: impl,
  };
}
